import type { CSSProperties } from "react";

/**
 * The one geometry for a status pill — the small coloured `<Tag>` that reports
 * an order / payment / shipment / account state.
 *
 * It was hand-written at thirteen call sites across the order, product,
 * transaction and user surfaces, and no two agreed. Between them they used
 * three font sizes (11 / 12 / 13), five paddings (`2px 8px`, `2px 10px`,
 * `3px 12px`, `4px 10px`, `4px 12px`), two weights (500 / 600) and three radii
 * (`"6px"`, `6`, `"4px"`) — so the same status rendered a different shape
 * depending on which page a vendor was looking at.
 *
 * Only the colour varies now. Geometry comes from here.
 *
 * @example outline pill — transparent, 1px border in the status colour
 *   <Tag bordered={false} style={statusTagStyle({ color: config.color })}>
 *
 * @example tinted pill — soft fill, no border
 *   <Tag bordered={false} style={statusTagStyle({ color, background: bg })}>
 */
export interface StatusTagStyleOptions {
  /** Text colour, and the border colour for the outline variant. */
  color?: string;
  /** Soft fill behind the label. Providing one switches off the border. */
  background?: string;
  /** Force the border on/off. Defaults to "on unless a background is given". */
  bordered?: boolean;
  /** Capitalise a raw enum value (`PENDING` → `Pending`). */
  capitalize?: boolean;
  /** Align an icon rendered through the Tag's `icon` prop. */
  withIcon?: boolean;
}

export function statusTagStyle({
  color,
  background,
  bordered,
  capitalize,
  withIcon,
}: StatusTagStyleOptions = {}): CSSProperties {
  const drawBorder = bordered ?? background === undefined;
  return {
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 600,
    padding: "3px 12px",
    // 6px: the pill rung. Smaller than the 10px card radius so a pill inside a
    // table row doesn't compete with the card containing it, larger than the
    // 4px chip rung so it still reads as a label rather than a tag.
    borderRadius: "var(--radius-pill)",
    color,
    background: background ?? "transparent",
    border: drawBorder && color ? `1px solid ${color}` : "none",
    ...(capitalize ? { textTransform: "capitalize" as const } : null),
    ...(withIcon
      ? { display: "inline-flex", alignItems: "center" as const }
      : null),
  };
}
